// GH-766 — "באנר ראשי": edit the EXISTING general-banner + lottery-popup settings from one admin page
// (instead of editing Mongo by hand). It never creates settings: a key that doesn't exist in this
// environment's `settings` collection is simply not shown. Values keep their current type
// (boolean → switch, text → text field, object → JSON). Images upload at original quality.
import { Alert, Box, Button, Card, CardContent, CircularProgress, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import constants from '../../constants';
import BannerImagePicker, { authHeaders } from './BannerImagePicker';

type Field = { key: string; label: string; hint?: string; image?: boolean; multiline?: boolean };
type SettingDoc = { _id: string; id?: string; key: string; value: any };

const SECTIONS: { title: string; note?: string; fields: Field[] }[] = [
	{
		title: 'באנר ראשי',
		note: 'הבאנר שמופיע בדף הבית לכל החברות שאין להן באנר פרטי.',
		fields: [
			{ key: 'show_main_banner', label: 'הצג באנר ראשי' },
			{ key: 'main_banner_image_link', label: 'תמונה בסיסית', hint: 'האתר בטלפון; באפליקציה — רק כשאין תמונת אפליקציה', image: true },
			{ key: 'main_banner_image_link_mobile', label: 'תמונה לאפליקציה (טלפון וטאבלט)', image: true },
			{ key: 'main_banner_image_link_desktop', label: 'תמונה לאתר במחשב (מ-1000 פיקסלים)', image: true },
			{ key: 'main_banner_image_link_en', label: 'תמונה באנגלית', image: true },
			{ key: 'main_banner_image_link_en_desktop', label: 'תמונה באנגלית — אתר במחשב', image: true },
		],
	},
	{
		title: 'לחיצה על הבאנר',
		fields: [
			{ key: 'banner_navigate', label: 'לחיצה על הבאנר מנווטת' },
			{ key: 'banner_navigate_name_new', label: 'לאן מנווטים (מסך באפליקציה או קישור)' },
			{ key: 'external_link', label: 'קישור חיצוני (נפתח בדפדפן)' },
			{ key: 'banner_navigate_params_new', label: 'פרמטרים למסך' },
		],
	},
	{
		title: 'פופאפ הגרלה',
		note: 'נפתח בלחיצה על הבאנר רק כשהניווט כבוי והפופאפ דלוק.',
		fields: [
			{ key: 'show_banner_popup', label: 'הצג פופאפ הגרלה' },
			{ key: 'main_banner_image_popup', label: 'פופאפ כתמונה אחת (אם מלא — במקום כותרות וכפתור)', image: true },
			{ key: 'banner_popup_image', label: 'תמונה בראש הפופאפ', image: true },
			{ key: 'banner_popup_title1', label: 'כותרת — שורה 1' },
			{ key: 'banner_popup_title2', label: 'כותרת — שורה 2' },
			{ key: 'banner_popup_text', label: 'טקסט ההסבר', multiline: true },
			{ key: 'banner_popup_btn_text', label: 'טקסט הכפתור' },
			{ key: 'banner_popup_info', label: 'אותיות קטנות', multiline: true },
			{ key: 'takanon', label: 'קישור לתקנון' },
			{ key: 'banner_text_share', label: 'טקסט השיתוף בוואטסאפ (הקישור נוסף אחריו)', multiline: true },
		],
	},
];
const ALL_KEYS = SECTIONS.flatMap((s) => s.fields.map((f) => f.key));

const toEditable = (v: any) => (v !== null && typeof v === 'object' ? JSON.stringify(v) : v);

const MainBannerPage = () => {
	const notify = useNotify();
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [docs, setDocs] = useState<Record<string, SettingDoc>>({});
	const [values, setValues] = useState<Record<string, any>>({});

	const load = async () => {
		setLoading(true);
		try {
			// GET setting/banner (admin + superadmin) returns only the existing banner/popup keys
			const res = await axios.get(`${constants.API_URL}setting/banner`, { headers: authHeaders() });
			const byKey: Record<string, SettingDoc> = {};
			(res.data as SettingDoc[]).filter((d) => ALL_KEYS.includes(d.key)).forEach((d) => (byKey[d.key] = d));
			setDocs(byKey);
			setValues(Object.fromEntries(Object.values(byKey).map((d) => [d.key, toEditable(d.value)])));
		} catch (err: any) {
			notify(err?.response?.data?.message || 'טעינת ההגדרות נכשלה', { type: 'error' });
		} finally {
			setLoading(false);
		}
	};
	useEffect(() => {
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const changed = useMemo(
		() => Object.keys(docs).filter((k) => toEditable(docs[k].value) !== values[k]),
		[docs, values],
	);

	const save = async () => {
		// validate JSON fields before writing anything
		for (const k of changed) {
			const orig = docs[k].value;
			if (orig !== null && typeof orig === 'object') {
				try {
					JSON.parse(values[k]);
				} catch {
					notify(`הערך של "${k}" אינו JSON תקין`, { type: 'error' });
					return;
				}
			}
		}
		setSaving(true);
		try {
			for (const k of changed) {
				const orig = docs[k].value;
				const value = orig !== null && typeof orig === 'object' ? JSON.parse(values[k]) : values[k];
				await axios.put(`${constants.API_URL}setting/banner/${k}`, { value }, { headers: authHeaders() });
			}
			notify(`נשמר (${changed.length} שדות)`, { type: 'success' });
			await load();
		} catch (err: any) {
			notify(err?.response?.data?.message || 'השמירה נכשלה', { type: 'error' });
		} finally {
			setSaving(false);
		}
	};

	const renderField = (f: Field) => {
		const doc = docs[f.key];
		if (!doc) return null; // key doesn't exist in this environment — never create it
		const v = values[f.key];
		const set = (nv: any) => setValues((p) => ({ ...p, [f.key]: nv }));
		if (typeof doc.value === 'boolean') {
			return (
				<Box key={f.key} sx={{ mb: 1 }}>
					<FormControlLabel control={<Switch checked={!!v} onChange={(e) => set(e.target.checked)} />} label={f.label} />
				</Box>
			);
		}
		if (f.image) {
			return <BannerImagePicker key={f.key} label={f.label} hint={f.hint} value={v || ''} onChange={set} disabled={saving} />;
		}
		const isJson = doc.value !== null && typeof doc.value === 'object';
		return (
			<TextField
				key={f.key}
				label={f.label}
				helperText={isJson ? 'JSON' : f.hint}
				value={v ?? ''}
				onChange={(e) => set(e.target.value)}
				fullWidth
				multiline={f.multiline || isJson}
				minRows={f.multiline ? 3 : 1}
				size="small"
				sx={{ mb: 2 }}
				disabled={saving}
			/>
		);
	};

	if (loading) {
		return (
			<Box sx={{ p: 4, textAlign: 'center' }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box sx={{ p: 2, maxWidth: 900 }} dir="rtl">
			<Title title="באנר ראשי" />
			<Typography variant="h5" sx={{ mb: 1 }}>
				באנר ראשי ופופאפ הגרלה
			</Typography>
			<Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
				עריכת ההגדרות הקיימות בלבד. באנר פרטי של חברה נערך בטאב "באנר" בעריכת החברה.
			</Typography>
			{SECTIONS.map((s) => {
				const fields = s.fields.filter((f) => docs[f.key]);
				if (!fields.length) return null;
				return (
					<Card key={s.title} sx={{ mb: 2 }}>
						<CardContent>
							<Typography variant="h6">{s.title}</Typography>
							{s.note && (
								<Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
									{s.note}
								</Typography>
							)}
							{fields.map(renderField)}
						</CardContent>
					</Card>
				);
			})}
			{!Object.keys(docs).length && <Alert severity="warning">לא נמצאו הגדרות באנר בסביבה הזו.</Alert>}
			<Box sx={{ position: 'sticky', bottom: 0, py: 2, background: '#fff', display: 'flex', gap: 2, alignItems: 'center' }}>
				<Button variant="contained" onClick={save} disabled={!changed.length || saving}>
					{saving ? <CircularProgress size={20} color="inherit" /> : 'שמירה'}
				</Button>
				<Typography variant="caption" color="textSecondary">
					{changed.length ? `${changed.length} שינויים שלא נשמרו` : 'אין שינויים'}
				</Typography>
			</Box>
		</Box>
	);
};

export default MainBannerPage;
